import {
  Button,
  FormHelperText,
  Step,
  StepLabel,
  Stepper,
  Switch
} from "@mui/material";
import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BranchStatus } from "../../../../constants/branch-status";
import { useToast } from "../../../../context/toast-context";
import { BranchService } from "../../../../services/branch-service";
import { AddBranchDTO, BranchBasicInfo, BranchSettings, ManagerInfo } from "../../../../services/dto/request/branches-request-dto";
import { PaycloudBranch } from "../../../../types";
import { compressImage } from "../../../../utils/compress-image";
import { WarningDraftBranch } from "../../../branches/warning-draft-branch";
import { LoadingModal } from "../../../LoadingModal";
import { ButtonForm } from "../../button-form";
import { ManagerFields } from "../../ManagerFields";
import { BranchInfoStepAR } from "./AR/branch-info-step-ar";
import { BranchInfoStepDO } from "./DO/branch-info-step-do";
import { validateStepOne, validateStepTwo } from "./validations";
import { refactorLink } from "../../../../utils/links";
import { useAuth } from "../../../../context/auth-context";
import { getCountryCode, COUNTRIES } from "../../../../constants/countries";
import { getInitialBranchSettings } from "./initial-values-by-country";
import { buildBranchParamsByCountry } from "./build-branch-params-by-country";

const switchStyles = {
  "& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track": {
    bgcolor: "#EF0BB8",
  },
  "& .MuiSwitch-track": {
    bgcolor: "#898989",
  },
  "& .MuiSwitch-thumb": {
    bgcolor: "#EF0BB8",
  },
};

interface AddBranchFormProps {
  setIsModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  addBranch?: (newBranch: PaycloudBranch[]) => void;
  withSplitTip?: boolean;
}

export function AddBranchForm({
  setIsModalOpen,
  addBranch,
  withSplitTip = false,
}: AddBranchFormProps) {
  const { t } = useTranslation();
  const branchService = useMemo(() => new BranchService(), []);
  const { showToast } = useToast();
  const { country } = useAuth();
  const countryCode = getCountryCode(country);
  const isDominicanRepublic = countryCode === COUNTRIES.DO;
  const [loading, setLoading] = useState(false);
  const [showDraftWarning, setShowDraftWarning] = useState(false);

  const [branchBasicInfo, setBranchBasicInfo] = useState<BranchBasicInfo>({
    name: "",
    address: "",
    phone: "",
    floor: "",
    postalCode: "",
    department: "",
    branchProvinceId: 0,
    branchCityId: 0,
    sameAddress: true,
    activityAddress: "",
    activityFloor: "",
    activityDepartment: "",
    activityPostalCode: "",
    activityProvinceId: 0,
    activityCityId: 0,
    afipFile: null,
    birthDate: "",
    occupation: "",
    ivaCondition: "",
    isPEP: false,
    pepPosition: "",
    isUIF: false,
    gender: "",
    civilStatus: "",
  });

  const [branchSettings, setBranchSettings] = useState<BranchSettings>(
    getInitialBranchSettings(countryCode)
  );

  const [managerInfo, setManagerInfo] = useState<ManagerInfo>({
    isInCharge: true,
    managerName: "",
    managerLastName: "",
    managerPhone: "",
    managerAddress: "",
    managerDocumentType: "CUIL",
    managerDocumentNumber: "",
    managerProvinceId: 0,
    managerCityId: 0,
    managerDniFront: null,
    managerDniBack: null,
    managerSelfie: null,
  });

  // En República Dominicana, isInCharge siempre debe estar en true
  useEffect(() => {
    if (isDominicanRepublic && !managerInfo.isInCharge) {
      setManagerInfo(prev => ({ ...prev, isInCharge: true }));
    }
  }, [isDominicanRepublic, managerInfo.isInCharge]);

  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const steps = [
    t("branches.create.step-1"),
    t("branches.create.step-2")
  ];

  const handleNext = () => {
    if (activeStep === 0) {
      const isValid = validateStepOne(branchBasicInfo, branchSettings, setErrors, t, undefined, undefined, false, isDominicanRepublic, countryCode);
      if (isValid) {
        setManagerInfo(prev => ({
          ...prev,
          managerProvinceId: prev.managerProvinceId || 0,
          managerCityId: prev.managerCityId || 0
        }));
        setActiveStep((prevStep) => prevStep + 1);
      } else {
        scrollToError();
      }
    }
  };

  const scrollToError = () => {
    setTimeout(() => {
      const firstErrorElement = document.querySelector('.Mui-error, [class*="text-red"]');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleBranchInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBranchBasicInfo((prev) => ({...prev, [e.target.name]: e.target.value}));
    if(errors[e.target.name]) {
      setErrors((prev) => {
        const newErrors = {...prev};
        delete newErrors[e.target.name];
        return newErrors;
      })
    }
  };

  const handleSettingsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBranchSettings((prev) => {
      const newSettings = { ...prev, [e.target.name]: e.target.value };

      if (e.target.name === "personType") {
        newSettings.identityType = e.target.value === "PHYSICAL" ? "CUIL" : "CUIT";
      }

      return newSettings;
    });
    if(e.target.name === "personType" || e.target.name === "identityType") {
      setErrors((prev) => {
        const newErrors = {...prev};
        delete newErrors.identityNumber;
        return newErrors;
      })
    }
    if (errors[e.target.name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[e.target.name];
        return newErrors;
      });
    }
  };

  const handleManagerInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setManagerInfo((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[e.target.name];
        return newErrors;
      });
    }
  };

  const handleSwitchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "branchConfig" | "manager" | "branchBasicInfo"
  ) => {
    if (type === "branchConfig") {
      setBranchSettings((prev) => ({...prev, [e.target.name]: e.target.checked}));
    } else if (type === "manager") {
      setManagerInfo((prev) => ({...prev, [e.target.name]: e.target.checked}));
    } else {
      setBranchBasicInfo((prev) => ({...prev, [e.target.name]: e.target.checked}));
    }
  };

  const handleDraftClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setShowDraftWarning(true);
  };

  const handleDraftConfirm = () => {
    setShowDraftWarning(false);
    handleSubmit(new Event('submit') as any, true);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>, isDraft = false) => {
    e.preventDefault();
    if (managerInfo.isInCharge && activeStep === 0) {
      const isValid = validateStepOne(branchBasicInfo, branchSettings, setErrors, t, undefined, undefined, false, isDominicanRepublic, countryCode);
      if (!isValid) {
        scrollToError();
        return;
      }
    }

    if (!isDraft && !managerInfo.isInCharge && activeStep === 1) {
      const isValid = validateStepTwo(managerInfo, setErrors, t);
      if (!isValid) {
        scrollToError();
        return;
      }
    }
    setLoading(true);

    const updatedReviewLink = refactorLink(branchSettings.googleReviewLink);

    // Construir parámetros base según el país
    const baseParams = buildBranchParamsByCountry({
      countryCode,
      branchBasicInfo,
      branchSettings,
      withSplitTip,
      updatedReviewLink,
      withTipAccount: branchSettings.createCBU,
      isDraft,
      status: branchSettings.createCBU ? BranchStatus.PENDING : BranchStatus.APPROVED,
    });

    let params: AddBranchDTO = {
      ...baseParams,
      IsInCharge: managerInfo.isInCharge,
    };

    // Si no es la persona a cargo, agregamos la información del manager
    if (!managerInfo.isInCharge) {
      Object.assign(params, {
        managerName: managerInfo.managerName,
        managerLastName: managerInfo.managerLastName,
        managerPhone: managerInfo.managerPhone,
        managerAddress: managerInfo.managerAddress,
        managerProvinceId: managerInfo.managerProvinceId,
        managerCityId: managerInfo.managerCityId,
        managerDniFront: managerInfo.managerDniFront ? await compressImage(managerInfo.managerDniFront) : null,
        managerDniBack: managerInfo.managerDniBack ? await compressImage(managerInfo.managerDniBack) : null,
        managerSelfie: managerInfo.managerSelfie ? await compressImage(managerInfo.managerSelfie) : null,
        managerDocumentType: managerInfo.managerDocumentType,
        managerDocumentNumber: managerInfo.managerDocumentNumber,
      });
    }

    const managerDataIncompleted = Object.entries(managerInfo).some(([key, value]) => value === null);
    if(!managerInfo.isInCharge && managerDataIncompleted) {
      params.Status = BranchStatus.INCOMPLETE;
    }

    await branchService.addBranch(
      params,
      async () => {
        await branchService.getBranches(
          {
            limit: 100,
            offset: 0,
          },
          (data) => {
            addBranch?.(data.branches);
            if(!managerInfo.isInCharge && managerDataIncompleted) {
              //TODO: Traducir mensaje
              showToast("Sucursal creada. Recuerda completar los datos del responsable en la configuración de la sucursal", "warning");
              setIsModalOpen?.(false);
              setLoading(false);
            } else {
              showToast(t("branches.create.success"), "success");
              setIsModalOpen?.(false);
              setLoading(false);
            }          },
          (error) => {
            showToast(error, "error");
            setLoading(false);
          }
        );
        
        
      },
      (error) => {
        showToast(error, "error");
        setLoading(false);
      }
    );
  };

  return (
    <>
      <LoadingModal isOpen={loading} />
      {showDraftWarning && (
        <WarningDraftBranch 
          open={showDraftWarning}
          onCancel={() => setShowDraftWarning(false)}
          onConfirm={handleDraftConfirm}
        />
      )}
      <form
        id="branch-form"
        onSubmit={handleSubmit}
        className="flex flex-col h-full"
      >
        {!managerInfo.isInCharge && (
          <Stepper
            activeStep={activeStep}
            className="mb-8"
            sx={{
              "& .MuiStepLabel-label": {
                fontFamily: "Montserrat",
              },
              "& .MuiStepLabel-root .Mui-completed": {
                color: "#EF0BB8",
              },
              "& .MuiStepLabel-root .Mui-active": {
                color: "#EF0BB8",
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        <div className="flex-1">
          {activeStep === 0 ? (
            <>
              {isDominicanRepublic ? (
                <BranchInfoStepDO
                  branchBasicInfo={branchBasicInfo}
                  branchSettings={branchSettings}
                  errors={errors}
                  handleBranchBasicInfoChange={handleBranchInfoChange}
                  handleBranchSettingsChange={handleSettingsChange}
                  handleSwitchChange={handleSwitchChange}
                  withSplitTip={withSplitTip}
                  disabled={loading}
                  isEditMode={false}
                />
              ) : (
                <BranchInfoStepAR
                  branchBasicInfo={branchBasicInfo}
                  branchSettings={branchSettings}
                  errors={errors}
                  handleBranchBasicInfoChange={handleBranchInfoChange}
                  handleBranchSettingsChange={handleSettingsChange}
                  handleSwitchChange={handleSwitchChange}
                  withSplitTip={withSplitTip}
                  disabled={loading}
                  isEditMode={false}
                />
              )}
              {branchSettings.createCBU && (
                <div className="w-full mt-2 flex justify-between items-center">
                  <div className="flex flex-col">
                    <label htmlFor="isInCharge" className="text-black">
                      {t("branches.create.legal-representative-question")}
                    </label>
                    <FormHelperText sx={{ fontFamily: "Montserrat" }}>
                      {t("branches.create.legal-representative-question-note")}
                    </FormHelperText>
                  </div>
                  <Switch
                    id="isInCharge"
                    name="isInCharge"
                    disabled={loading || isDominicanRepublic}
                    checked={managerInfo.isInCharge}
                    onChange={(e) => handleSwitchChange(e, "manager")}
                    sx={switchStyles}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="space-y-4">
              <ManagerFields
                disabled={managerInfo.isInCharge || loading}
                errors={errors}
                handleManagerInfoChange={handleManagerInfoChange}
                managerInfo={managerInfo}
              />
            </div>
          )}
        </div>

        <div className="flex justify-between mt-8 gap-4">
          {!managerInfo.isInCharge ? (
            <div className="w-full flex flex-col gap-4">
              {activeStep === steps.length - 1 && (
                <Button
                  onClick={handleDraftClick}
                  disabled={loading}
                  sx={{
                    color: "#fff",
                    fontFamily: "Montserrat",
                    fontWeight: 300,
                    background: "#C2C8D2",
                    border: "1px solid #C2C8D2",
                    borderRadius: "16px",
                    padding: "2px 8px",
                    width: "100%",
                    textTransform: "none",
                  }}
                >
                  Guardar como borrador
                </Button>
              )}
              <div className="flex gap-4">
                <Button
                  onClick={handleBack}
                  disabled={activeStep === 0 || loading}
                  sx={{
                    color: "#EF0BB8",
                    fontFamily: "Montserrat",
                    fontWeight: 300,
                    background: "#fff",
                    border: "1px solid #EF0BB8",
                    borderRadius: "16px",
                    padding: "2px 8px",
                    flex: 1,
                    textTransform: "none",
                    "&.Mui-disabled": {
                      opacity: 0,
                    },
                  }}
                >
                  {t("branches.create.back")}
                </Button>

                {activeStep === steps.length - 1 ? (
                  <div className="flex-1">
                    <ButtonForm loading={loading}>
                      {t("branches.create.submit")}
                    </ButtonForm>
                  </div>
                ) : (
                  <Button
                    onClick={handleNext}
                    disabled={loading}
                    sx={{
                      color: "#fff",
                      fontFamily: "Montserrat",
                      fontWeight: 300,
                      background: "#EF0BB8",
                      border: "1px solid #EF0BB8",
                      borderRadius: "16px",
                      padding: "2px 8px",
                      flex: 1,
                      textTransform: "none",
                      "&:hover": {
                        background: "#d109a3",
                      },
                    }}
                  >
                    {t("branches.create.next")}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="w-full flex flex-col gap-4">
              <ButtonForm loading={loading}>
                {t("branches.create.submit")}
              </ButtonForm>
            </div>
          )}
        </div>
        {!isDominicanRepublic && (
          <span className="text-xs text-center pt-2 text-gray-600 font-medium">
            {t("branches.create.note")}
          </span>
        )}
      </form>
    </>
  );
}
